import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { Notifier } from '../../system/services/notificationManager';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import {
  Organization,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useOrganizationParentOptionsQuery,
} from '../../../types/generated-types';
import { useNavigate } from 'react-router-dom';
import BorderedSection from '../shared/borderedSection';
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from 'libphonenumber-js';
import { countries } from 'countries-list';

interface Country {
  code: string;
  name: string;
}

const countryOptions: Country[] = Object.entries(countries).map(
  ([code, country]) => ({
    code,
    name: (country as { name: string }).name,
  }),
);

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  hqAddress: Yup.object().shape({
    line1: Yup.string().required('Address line 1 is required'),
    line2: Yup.string(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    postalCode: Yup.string().required('Postal code is required'),
    country: Yup.string().required('Country is required'),
  }),
  hqPhone: Yup.string()
    .required('Phone is required')
    .test('valid-phone', 'Invalid phone number', (value) => {
      if (!value) return false;
      return isValidPhoneNumber(value) || false;
    }),
  hqEmail: Yup.string()
    .required('Email is required')
    .email('Invalid email format'),
  parentOrganizationId: Yup.string(),
});

interface FormValues {
  name: string;
  description: string;
  hqAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  hqPhone: string;
  hqEmail: string;
  parentOrganizationId: string;
}

const calculateOrganizationFormValues = (
  organization?: Partial<Organization>,
): FormValues => ({
  name: organization?.name || '',
  description: organization?.description || '',
  hqAddress: {
    line1: organization?.hqAddress?.line1 || '',
    line2: organization?.hqAddress?.line2 || '',
    city: organization?.hqAddress?.city || '',
    state: organization?.hqAddress?.state || '',
    postalCode: organization?.hqAddress?.postalCode || '',
    country: organization?.hqAddress?.country || '',
  },
  hqPhone: organization?.hqPhone || '',
  hqEmail: organization?.hqEmail || '',
  parentOrganizationId: organization?.parentOrganization?._id || '',
});

interface OrganizationDetailFormProps {
  organization?: Partial<Organization>;
  loading?: boolean;
  error?: Error;
}

export const OrganizationDetailForm: React.FC<OrganizationDetailFormProps> = ({
  organization,
  loading,
  error,
}) => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const [createOrganization] = useCreateOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();

  const { data: parentOptions, loading: parentOptionsLoading } =
    useOrganizationParentOptionsQuery({
      variables: { excludeId: organization?._id || '' },
    });

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = parsePhoneNumberFromString(value);
    return phoneNumber ? phoneNumber.formatInternational() : value;
  };

  const initialValues = calculateOrganizationFormValues(organization);

  const onSubmit = async (values: FormValues) => {
    try {
      if (organization?._id) {
        if (!can('update', 'Organization')) {
          Notifier.error('You do not have permission to update organizations');
          return;
        }

        await updateOrganization({
          variables: {
            id: organization._id,
            organization: {
              ...values,
            },
          },
        });

        Notifier.success('Organization updated successfully');
      } else {
        if (!can('create', 'Organization')) {
          Notifier.error('You do not have permission to create organizations');
          return;
        }

        const result = await createOrganization({
          variables: {
            organization: values,
          },
        });

        if (result.data?.createOrganization?._id) {
          Notifier.success('Organization created successfully');
          navigate(`/organizations`);
        } else {
          Notifier.error('Organization created failed');
        }
      }
    } catch (error) {
      Notifier.error('Error saving organization');
      console.error(error);
    }
  };

  if (loading || parentOptionsLoading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">
          Error loading organization: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BorderedSection title="Organization Information">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      multiline
                      rows={4}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="parentOrganizationId"
                      label="Parent Organization"
                      value={values.parentOrganizationId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.parentOrganizationId &&
                        Boolean(errors.parentOrganizationId)
                      }
                      helperText={
                        touched.parentOrganizationId &&
                        errors.parentOrganizationId
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(parentOptions?.organizations ?? []).map((org) => {
                        return org ? (
                          <MenuItem key={org._id} value={org._id}>
                            {org.name}
                          </MenuItem>
                        ) : (
                          <div>&nbsp;</div>
                        );
                      })}
                    </TextField>
                  </Grid>
                </Grid>
              </BorderedSection>
            </Grid>

            <Grid item xs={12}>
              <BorderedSection title="Headquarters Address">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="hqAddress.line1"
                      label="Address Line 1"
                      value={values.hqAddress.line1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.hqAddress?.line1 &&
                        Boolean(errors.hqAddress?.line1)
                      }
                      helperText={
                        touched.hqAddress?.line1 && errors.hqAddress?.line1
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="hqAddress.line2"
                      label="Address Line 2"
                      value={values.hqAddress.line2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.hqAddress?.line2 &&
                        Boolean(errors.hqAddress?.line2)
                      }
                      helperText={
                        touched.hqAddress?.line2 && errors.hqAddress?.line2
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="hqAddress.city"
                      label="City"
                      value={values.hqAddress.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.hqAddress?.city &&
                        Boolean(errors.hqAddress?.city)
                      }
                      helperText={
                        touched.hqAddress?.city && errors.hqAddress?.city
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="hqAddress.state"
                      label="State"
                      value={values.hqAddress.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.hqAddress?.state &&
                        Boolean(errors.hqAddress?.state)
                      }
                      helperText={
                        touched.hqAddress?.state && errors.hqAddress?.state
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="hqAddress.postalCode"
                      label="Postal Code"
                      value={values.hqAddress.postalCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.hqAddress?.postalCode &&
                        Boolean(errors.hqAddress?.postalCode)
                      }
                      helperText={
                        touched.hqAddress?.postalCode &&
                        errors.hqAddress?.postalCode
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      name="hqAddress.country"
                      label="Country"
                      value={values.hqAddress.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.hqAddress?.country &&
                        Boolean(errors.hqAddress?.country)
                      }
                      helperText={
                        touched.hqAddress?.country && errors.hqAddress?.country
                      }
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </BorderedSection>
            </Grid>

            <Grid item xs={12}>
              <BorderedSection title="Contact Information">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="hqPhone"
                      label="Phone"
                      placeholder="+1 (123) 456-7890"
                      value={values.hqPhone}
                      onChange={async (e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        await setFieldValue('hqPhone', formatted);
                      }}
                      onBlur={handleBlur}
                      error={touched.hqPhone && Boolean(errors.hqPhone)}
                      helperText={touched.hqPhone && errors.hqPhone}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="hqEmail"
                      label="Email"
                      value={values.hqEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.hqEmail && Boolean(errors.hqEmail)}
                      helperText={touched.hqEmail && errors.hqEmail}
                    />
                  </Grid>
                </Grid>
              </BorderedSection>
            </Grid>

            <Grid item xs={12} mb={2} mr={2}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor: '#36AFA5',
                    '&:hover': { backgroundColor: '#2a7f7c' },
                  }}
                  disabled={isSubmitting}
                >
                  {organization?._id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
